import { useEffect, useRef, useState } from "react";
import DateTimeBadge from "./DateTimeBadge";
import { toMinutes } from "@/lib/display";

// Clock-driven auto-scroll: derives its position from the shared `elapsed`
// offset (ms into this view) so all display instances scroll in lockstep.
// Holds at top for topMs, scrolls to bottom over scrollMs, holds at bottom.
// Does nothing when the content fits without overflow.
function AutoScroll({ children, elapsed = 0, topMs = 10000, scrollMs = 10000 }) {
  const outer = useRef(null);
  const inner = useRef(null);
  const [state, setState] = useState({ y: 0, anim: false });
  useEffect(() => {
    const o = (inner.current?.scrollHeight || 0) - (outer.current?.clientHeight || 0);
    const target = o <= 0 ? { y: 0, anim: false } : (elapsed >= topMs ? { y: -o, anim: true } : { y: 0, anim: false });
    setState((s) => (s.y === target.y && s.anim === target.anim ? s : target));
  });
  return (
    <div ref={outer} className="h-full overflow-hidden">
      <div ref={inner} style={{ transform: `translateY(${state.y}px)`, transition: state.anim ? `transform ${scrollMs}ms linear` : "none", willChange: "transform" }}>
        {children}
      </div>
    </div>
  );
}

function Header({ settings, label, large }) {
  return (
    <div className="flex items-center justify-between border-b-4 border-[#E5FF00] pb-6 mb-8 shrink-0">
      <h1 className={`font-display font-black uppercase tracking-wide ${large ? "text-8xl" : "text-6xl"}`}>{settings.event_title}</h1>
      <div className="flex items-center gap-8">
        <span className={`font-display uppercase tracking-[0.3em] text-[#00F0FF] ${large ? "text-5xl" : "text-3xl"}`}>{label}</span>
        <DateTimeBadge show={settings.show_datetime} />
      </div>
    </div>
  );
}

function Standings({ standings, large }) {
  const cols = standings.columns || [];
  const rows = standings.rows || [];
  return (
    <table className="w-full border-collapse" data-testid="display-standings">
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i} className={`font-display uppercase tracking-wider ${large ? "text-4xl" : "text-2xl"} text-[#8A99AD] pb-4 border-b-2 border-[#1E2638] ${i === 1 ? "text-left pl-4" : "text-center"}`}>
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={ri % 2 ? "bg-[#0b0e15]" : ""}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={`${large ? "py-7 text-6xl" : "py-5 text-4xl"} font-mono-num ${ci === 1 ? "text-left pl-4 font-display font-bold tracking-wide" : "text-center"} ${
                  ci === 0 ? "text-[#E5FF00] font-bold" : cols[ci] === "Pkt" || cols[ci] === "Punkte" ? "text-[#00F0FF] font-bold" : "text-white"
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Schedule({ matches, groupsById, fieldColors, elapsed, large }) {
  const sorted = [...(matches || [])].sort((a, b) => (toMinutes(a.start_time) ?? 9999) - (toMinutes(b.start_time) ?? 9999));
  return (
    <AutoScroll elapsed={elapsed}>
    <div className="flex flex-col gap-4 content-start" data-testid="display-schedule">
      {sorted.map((m) => {
        const done = m.status === "Beendet" && m.score1 != null;
        const gname = m.group_id ? groupsById[m.group_id] : null;
        return (
          <div key={m.id} className={`flex items-center gap-4 bg-[#0b0e15] border border-[#1E2638] rounded-xl px-6 ${large ? "py-6" : "py-4"}`}>
            <span className={`font-mono-num font-bold ${large ? "text-6xl w-56" : "text-4xl w-28"} text-[#E5FF00] shrink-0`}>{m.start_time || "--:--"}</span>
            {m.field ? <span className={`font-display font-black uppercase tracking-wider ${large ? "text-4xl" : "text-2xl"} rounded-lg px-4 py-1.5 shrink-0`} style={{ background: fieldColors[m.field] || "#00F0FF", color: "#0A0C10" }} data-testid={`display-field-${m.id}`}>{m.field}</span> : null}
            {gname ? <span className={`font-display uppercase tracking-wider ${large ? "text-2xl" : "text-lg"} text-[#8A99AD] shrink-0`}>{gname}</span> : null}
            <span className={`font-display font-bold ${large ? "text-5xl" : "text-3xl"} truncate flex-1 text-right`}>{m.team1_name}</span>
            <span className={`font-mono-num font-bold ${large ? "text-6xl min-w-[150px]" : "text-4xl min-w-[110px]"} px-3 text-center`} style={{ color: done ? "#fff" : "#3a465e" }}>
              {done ? `${m.score1}:${m.score2}` : "vs"}
            </span>
            <span className={`font-display font-bold ${large ? "text-5xl" : "text-3xl"} truncate flex-1`}>{m.team2_name}</span>
            <span
              className={`font-display uppercase tracking-wider ${large ? "text-2xl" : "text-lg"} px-3 py-1 rounded shrink-0 ${
                m.status === "Beendet" ? "text-[#8A99AD] bg-[#1E2638]" : m.status === "Läuft" ? "text-black bg-[#FF2A55]" : "text-[#00F0FF] border border-[#00F0FF]/40"
              }`}
            >
              {m.status}
            </span>
          </div>
        );
      })}
      {sorted.length === 0 && <p className="text-[#3a465e] text-2xl">Keine Spiele geplant</p>}
    </div>
    </AutoScroll>
  );
}

function Groups({ groups, groupStandings, elapsed, demo, large }) {
  const shown = demo ? groups : groups.filter((g) => (((groupStandings || {})[g.id]?.rows) || []).some((r) => (r.sp || 0) > 0));
  return (
    <AutoScroll elapsed={elapsed}>
      <div className="grid grid-cols-2 gap-10 content-start" data-testid="display-groups">
      {shown.map((g) => {
        const rows = groupStandings?.[g.id]?.rows || [];
        return (
          <div key={g.id} className="bg-[#0b0e15] border border-[#1E2638] rounded-2xl p-8">
            <h2 className={`font-display font-black uppercase tracking-wide ${large ? "text-5xl" : "text-4xl"} text-[#00F0FF] mb-4`}>{g.name}</h2>
            <table className="w-full">
              <thead>
                <tr className={`text-[#8A99AD] font-display uppercase ${large ? "text-2xl" : "text-xl"}`}>
                  <th className="w-10 text-left">#</th>
                  <th className="text-left">Mannschaft</th>
                  <th className="w-16 text-center">Sp</th>
                  <th className="w-24 text-center">Tore</th>
                  <th className="w-16 text-center">Diff</th>
                  <th className="w-16 text-center">Pkt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name} className={i % 2 ? "bg-[#0A0C10]" : ""}>
                    <td className={`${large ? "py-4 text-4xl" : "py-3 text-3xl"} font-mono-num text-[#E5FF00] font-bold`}>{i + 1}</td>
                    <td className={`${large ? "py-4 text-4xl" : "py-3 text-3xl"} font-display font-bold`}>{r.name}</td>
                    <td className={`text-center ${large ? "text-4xl" : "text-3xl"} font-mono-num`}>{r.sp}</td>
                    <td className={`text-center ${large ? "text-4xl" : "text-3xl"} font-mono-num`}>{r.tore}</td>
                    <td className={`text-center ${large ? "text-4xl" : "text-3xl"} font-mono-num`}>{r.diff}</td>
                    <td className={`text-center ${large ? "text-4xl" : "text-3xl"} font-mono-num text-[#00F0FF] font-bold`}>{r.pkt}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="text-[#3a465e] text-2xl py-3">Noch keine Ergebnisse</td></tr>}
              </tbody>
            </table>
          </div>
        );
      })}
      {groups.length === 0 && <p className="text-[#3a465e] text-2xl">Keine Gruppen</p>}
      </div>
    </AutoScroll>
  );
}

export default function DisplayTable({ snapshot, variant = "standings", matchdayId, elapsed = 0 }) {
  const { standings, settings, matches, groups, group_standings, matchdays, fields } = snapshot;
  const demo = settings?.demo_mode === true;
  const large = settings?.display_mode === "large";
  const groupsById = Object.fromEntries((groups || []).map((g) => [g.id, g.name]));
  const fieldColors = Object.fromEntries((fields || []).map((f) => [f.name, f.color || "#00F0FF"]));
  const mdName = matchdayId && matchdayId !== "none"
    ? ((matchdays || []).find((d) => d.id === matchdayId)?.name)
    : (matchdayId === "none" ? "Ohne Spieltag" : null);
  let sched = matches || [];
  if (variant === "schedule" && matchdayId) {
    sched = sched.filter((m) => (matchdayId === "none" ? !m.matchday_id : m.matchday_id === matchdayId));
  }
  const label = variant === "standings"
    ? (settings.standings_title || "Punktetabelle")
    : variant === "groups"
    ? "Gruppen"
    : (settings.schedule_title || "Spielplan") + (mdName ? ` · ${mdName}` : "");
  return (
    <div className="w-full h-full flex flex-col text-white p-14">
      <Header settings={settings} label={label} large={large} />
      <div className="flex-1 overflow-hidden">
        {variant === "standings" && <Standings standings={standings} large={large} />}
        {variant === "groups" && <Groups groups={groups || []} groupStandings={group_standings} elapsed={elapsed} demo={demo} large={large} />}
        {variant === "schedule" && <Schedule matches={sched} groupsById={groupsById} fieldColors={fieldColors} elapsed={elapsed} large={large} />}
      </div>
    </div>
  );
}
